/**
 * Nutriop Footer JavaScript assets/nutriop-footer.js
 * Handles Klaviyo integration, analytics, and enhanced functionality
 */

class NutriopFooter {
  constructor() {
    this.footer = document.querySelector('.nutriop-footer');
    if (!this.footer) return;
    
    this.init();
  }
  
  init() {
    this.setupNewsletterForm();
    this.trackFooterVisibility();
    this.enhanceAccessibility();
    this.setupSmoothScrolling();
  }
  
  setupNewsletterForm() {
    const forms = this.footer.querySelectorAll('[class^="klaviyo-form-"]');
    
    forms.forEach(form => {
      const emailInput = form.querySelector('.nutriop-footer__email-input');
      const submitBtn = form.querySelector('.nutriop-footer__submit-btn');
      const submitText = submitBtn.querySelector('.nutriop-footer__submit-text');
      const originalText = submitText.textContent;
      
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const email = emailInput.value.trim();
        
        // Validate email
        if (!this.validateEmail(email)) {
          this.showError(emailInput, 'Please enter a valid email address');
          return;
        }
        
        // Disable form during submission
        emailInput.disabled = true;
        submitBtn.disabled = true;
        submitText.textContent = 'Subscribing...';
        
        try {
          // Track newsletter signup
          this.trackNewsletterSignup(email);
          
          // Submit to Klaviyo
          await this.submitToKlaviyo(form, email);
          
          // Show success
          this.showSuccess(submitBtn, submitText, emailInput);
          
          // Reset form after delay
          setTimeout(() => {
            submitText.textContent = originalText;
            submitBtn.style.background = '';
            emailInput.disabled = false;
            submitBtn.disabled = false;
          }, 5000);
          
        } catch (error) {
          console.error('Newsletter signup error:', error);
          this.showError(emailInput, 'Something went wrong. Please try again.');
          submitText.textContent = originalText;
          emailInput.disabled = false;
          submitBtn.disabled = false;
        }
      });
      
      // Clear error on input
      emailInput.addEventListener('input', () => {
        this.clearError(emailInput);
      });
      
      // Enhanced keyboard support
      emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          form.dispatchEvent(new Event('submit'));
        }
      });
    });
  }
  
  validateEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }
  
  showError(input, message) {
    input.classList.add('error');
    input.setAttribute('aria-invalid', 'true');
    
    // Create or update error message
    let errorEl = input.parentElement.querySelector('.nutriop-footer__error');
    if (!errorEl) {
      errorEl = document.createElement('span');
      errorEl.className = 'nutriop-footer__error';
      errorEl.setAttribute('role', 'alert');
      input.parentElement.appendChild(errorEl);
    }
    errorEl.textContent = message;
  }
  
  clearError(input) {
    input.classList.remove('error');
    input.setAttribute('aria-invalid', 'false');
    
    const errorEl = input.parentElement.querySelector('.nutriop-footer__error');
    if (errorEl) {
      errorEl.remove();
    }
  }
  
  showSuccess(button, textElement, input) {
    textElement.textContent = '✓ Success! Check your email';
    button.style.background = '#00a585';
    input.value = '';
    
    // Announce success to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = 'Newsletter signup successful. Check your email.';
    document.body.appendChild(announcement);
    
    setTimeout(() => announcement.remove(), 1000);
  }
  
  async submitToKlaviyo(form, email) {
    const listId = form.querySelector('input[name="g"]').value;
    
    // Klaviyo identify
    if (typeof klaviyo !== 'undefined') {
      klaviyo.push(['identify', {
        '$email': email,
        'Newsletter Source': 'Footer',
        'Signup Date': new Date().toISOString(),
        'Signup URL': window.location.href,
        'User Agent': navigator.userAgent
      }]);
      
      // Track signup event
      klaviyo.push(['track', 'Newsletter Signup', {
        'List ID': listId,
        'Source': 'Footer Form',
        'Page': window.location.pathname,
        'Offer': '15% off + Longevity Guide'
      }]);
      
      // Subscribe to list
      klaviyo.push(['subscribe', {
        'list': listId,
        'email': email
      }]);
    }
    
    // Also submit form traditionally as backup
    const formData = new FormData(form);
    fetch(form.action, {
      method: 'POST',
      body: formData,
      mode: 'no-cors'
    });
  }
  
  trackNewsletterSignup(email) {
    const emailDomain = email.split('@')[1];
    
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'sign_up', {
        method: 'newsletter',
        location: 'footer',
        email_domain: emailDomain,
        offer_type: '15_percent_plus_guide'
      });
    }
    
    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead', {
        content_name: 'Newsletter Signup',
        content_category: 'Footer',
        value: 15, // 15% discount value
        currency: 'USD'
      });
    }
  }
  
  trackFooterVisibility() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Track footer view
          if (typeof gtag !== 'undefined') {
            gtag('event', 'view_item_list', {
              item_list_id: 'footer',
              item_list_name: 'Footer Navigation',
              items: this.getFooterLinks()
            });
          }
          
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(this.footer);
  }
  
  getFooterLinks() {
    const links = this.footer.querySelectorAll('.nutriop-footer__links a');
    return Array.from(links).slice(0, 10).map((link, index) => ({
      item_id: link.href,
      item_name: link.textContent,
      index: index,
      item_category: 'footer_link'
    }));
  }
  
  enhanceAccessibility() {
    // Add proper ARIA labels
    const columns = this.footer.querySelectorAll('.nutriop-footer__column');
    columns.forEach(column => {
      const title = column.querySelector('.nutriop-footer__column-title');
      const list = column.querySelector('.nutriop-footer__links');
      if (title && list) {
        const id = title.textContent.toLowerCase().replace(/\s+/g, '-');
        title.id = `footer-${id}`;
        list.setAttribute('aria-labelledby', title.id);
      }
    });
    
    // Enhance payment icons
    const paymentIcons = this.footer.querySelectorAll('.nutriop-footer__payment-icons svg');
    const paymentTypes = ['Visa', 'Mastercard', 'American Express', 'PayPal'];
    paymentIcons.forEach((icon, index) => {
      if (paymentTypes[index]) {
        icon.setAttribute('role', 'img');
        icon.setAttribute('aria-label', `${paymentTypes[index]} accepted`);
      }
    });
  }
  
  setupSmoothScrolling() {
    // Smooth scroll for footer links that point to anchors
    const anchorLinks = this.footer.querySelectorAll('a[href^="#"]');
    anchorLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          
          // Track internal link click
          if (typeof gtag !== 'undefined') {
            gtag('event', 'click', {
              event_category: 'Footer',
              event_label: link.textContent,
              transport_type: 'beacon'
            });
          }
        }
      });
    });
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new NutriopFooter();
  });
} else {
  new NutriopFooter();
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NutriopFooter;
}